import { useEffect } from 'react';

type ChatWidgetPosition = 'bottom-right' | 'bottom-left';

type VivaChatApi = {
  init: (options?: {
    brandId?: string;
    chainId?: string;
    position?: ChatWidgetPosition;
    bottomPadding?: number;
    sidePadding?: number;
  }) => void;
};

type BrandChatInfo = {
  bookingURL?: string;
  isChatWidgetEnabled?: boolean;
  chatWidgetPosition?: string;
  chatWidgetBottomPadding?: number;
  chatWidgetSidePadding?: number;
};

function trimTrailingSlash(url: string) {
  return url.replace(/\/$/, '');
}

function resolvePosition(raw?: string): ChatWidgetPosition {
  return raw === 'bottom-left' ? 'bottom-left' : 'bottom-right';
}

function toPadding(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : fallback;
}

/**
 * Loads the IBE chat bundle from the brand's bookingURL host
 * (`{bookingURL}/chat-widget/chat-widget.js`) onto the host page.
 * Visible only when brand `isChatWidgetEnabled` is true; position/padding
 * come from `chatWidgetPosition`, `chatWidgetBottomPadding`, `chatWidgetSidePadding`.
 */
export function useIbeChatWidget(
  brandId?: string,
  chainId?: string,
  brandInfo?: BrandChatInfo | null
) {
  useEffect(() => {
    if (!brandId || !brandInfo) return;

    let hostWindow: Window = window;
    try {
      if (window.parent && window.parent !== window) {
        void window.parent.document;
        hostWindow = window.parent;
      }
    } catch {
      // Cross-origin parent — keep current window.
    }

    const hostDoc = hostWindow.document;
    const SCRIPT_ID = 'ibe-chat-widget-script';
    const FRAME_ID = 'viva-chat-widget-frame';
    let bookingUrl = brandInfo.bookingURL;

    const removeChat = () => {
      hostDoc.getElementById(FRAME_ID)?.remove();
      hostDoc.getElementById(SCRIPT_ID)?.remove();
      delete (hostWindow as Window & { VivaChatWidget?: VivaChatApi }).VivaChatWidget;
    };

    if (!brandInfo.isChatWidgetEnabled) {
      removeChat();
      return;
    }

    if (!bookingUrl) return;
    const origin = typeof window !== undefined ? window?.origin : '';
    if (origin?.includes('localhost')) {
      bookingUrl = 'http://localhost:3000'
    }
    const CHAT_SCRIPT_SRC = `${trimTrailingSlash(bookingUrl)}/chat-widget/chat-widget.js`;
    const position = resolvePosition(brandInfo.chatWidgetPosition);
    const bottomPadding = toPadding(brandInfo.chatWidgetBottomPadding, 80);
    const sidePadding = toPadding(brandInfo.chatWidgetSidePadding, 10);

    const initChat = () => {
      (hostWindow as Window & { VivaChatWidget?: VivaChatApi }).VivaChatWidget?.init({
        brandId,
        chainId,
        position,
        bottomPadding,
        sidePadding,
      });
    };

    const existing = hostDoc.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (!(hostWindow as Window & { VivaChatWidget?: VivaChatApi }).VivaChatWidget) {
        existing.remove();
      } else {
        initChat();
        return;
      }
    }

    const script = hostDoc.createElement('script');
    script.id = SCRIPT_ID;
    script.src = CHAT_SCRIPT_SRC;
    script.async = true;
    script.onload = initChat;
    script.onerror = () => {
      console.error(
        '[IbeChat] Failed to load script:',
        CHAT_SCRIPT_SRC,
        '— check bookingURL /chat-widget/chat-widget.js'
      );
    };
    hostDoc.head.appendChild(script);
  }, [brandId, chainId, brandInfo]);
}
